/**
 * The `patient.select` action (F2 in Dentrix Familiar, Ctrl+P in Open
 * Dental and Eaglesoft Familiar, the toolbar's Select Patient button).
 * When the active mode has a `shell.selectPatient` slot (its own dialog,
 * spec S2.5) that dialog opens; otherwise the palette opens in patient mode.
 * Either way the same patient search API answers and the chosen patient
 * becomes the active patient.
 *
 * Also the app-wide patient verbs every mode's menus and keys share
 * (requests-eaglesoft 3, requests-dentrix M-3):
 *
 * - `patient.last`: the patient before the current one in the recent list
 *   (Eaglesoft F5 "Last Person"); on a patient screen it switches that
 *   screen to them, elsewhere it only makes them current.
 * - `patient.open.{family,account,chart,perio,txplan,images,notes}`: that
 *   screen for the current patient; with none, Select Patient first.
 * - `patients.newFamily`: the new household composer.
 *
 * The dialog's data (requests-dentrix I-14, requests-opendental R6,
 * requests-eaglesoft D3) comes from `POST /v1/shell/patients/select` (by
 * field, guarantors only, rich rows) and the server-side recent list, which
 * survives reloads and devices. A familiar mode records every patient made
 * current in that list; Velora Modern sends nothing new.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { SELECT_PATIENT_FIELDS, type SelectPatientRow } from '@velora/contracts/shell';
import { ApiError, getShellPatients } from '@velora/sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PATIENT_OPEN_TARGET_IDS,
  PATIENT_OPEN_TARGETS,
  defaultOpenTarget,
  fetchRecentPatients,
  fetchSelectPatients,
  lastPatientId,
  markRecentPatient,
  openTargetRoute,
  rowToHit,
  samePlaceFor,
  isGetAll,
  selectCriteria,
  selectLabel,
  type PatientOpenTarget,
  type RouteTarget,
} from '../../modules/patients/select-patient.js';
import { registerAction } from '../actions/registry.js';
import { api, clientFor } from '../api.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import {
  getCurrentPatient,
  setCurrentPatient,
  useCurrentPatient,
  useRecentPatients,
} from '../patient/current.js';
import { maskName, usePrivacy } from '../privacy/privacy.js';
import { usePermission } from '../session/session.js';
import { useSession as useIdentitySession } from '../../modules/identity/index.js';
import { SkinSlot, useSkinSlot, useTerm } from '../skins/SkinProvider.js';
import type {
  SelectPatientHit,
  SelectPatientModel,
  SelectPatientSearchOptions,
} from '../skins/types.js';

const RECENT_KEY = ['shell', 'recent-patients'] as const;

interface Known {
  label: string;
  chartNumber: string | null;
}

export function SelectPatientHost({ onPalette }: { onPalette: () => void }) {
  const [open, setOpen] = useState(false);
  const dialog = useSkinSlot('shell.selectPatient');
  const label = useTerm('selectPatient');
  const office = useOfficeScope();
  const navigate = useNavigate();
  const router = useRouter();
  const location = useRouterState({ select: (s) => s.location });
  const privacy = usePrivacy();
  // A stable mask per privacy state (usePrivacy returns a new object each render).
  const privacyOn = privacy.on;
  const mask = useMemo(() => (privacyOn ? maskName : (t: string) => t), [privacyOn]);
  const sessionRecent = useRecentPatients();
  const current = useCurrentPatient();
  const queryClient = useQueryClient();
  const canView = usePermission('patient.view');
  const canEdit = usePermission('patient.edit');
  const canSsn = usePermission('patient.view_ssn');
  const canTarget: Record<PatientOpenTarget, boolean> = {
    family: usePermission(PATIENT_OPEN_TARGETS.family.permission),
    account: usePermission(PATIENT_OPEN_TARGETS.account.permission),
    chart: usePermission(PATIENT_OPEN_TARGETS.chart.permission),
    perio: usePermission(PATIENT_OPEN_TARGETS.perio.permission),
    txplan: usePermission(PATIENT_OPEN_TARGETS.txplan.permission),
    images: usePermission(PATIENT_OPEN_TARGETS.images.permission),
    notes: usePermission(PATIENT_OPEN_TARGETS.notes.permission),
  };
  // Only a familiar mode (a mode with its own dialog) uses the server list.
  const familiar = dialog !== null && dialog !== undefined;

  /* ---------- server-side recent list ---------- */
  const recentQ = useQuery({
    // The office selector scopes the list too (one office: only its patients).
    queryKey: [...RECENT_KEY, { office: office.param }],
    queryFn: ({ signal }) => fetchRecentPatients(clientFor(office.param), signal),
    enabled: familiar && canView,
    staleTime: 30_000,
    retry: false,
  });
  const serverRows = recentQ.data?.hits ?? null;

  /** Unmasked labels of every patient the dialog has listed (setCurrentPatient wants them). */
  const known = useRef(new Map<string, Known>());
  const remember = useCallback((rows: readonly SelectPatientRow[]) => {
    for (const r of rows)
      known.current.set(r.id, { label: selectLabel(r), chartNumber: r.chartNumber });
  }, []);
  useEffect(() => {
    if (serverRows) remember(serverRows);
  }, [serverRows, remember]);
  for (const p of sessionRecent) {
    if (!known.current.has(p.id))
      known.current.set(p.id, { label: p.label, chartNumber: p.chartNumber });
  }

  // Every patient made current goes to the front of the server list.
  const currentId = current?.id ?? null;
  useEffect(() => {
    if (!familiar || !canView || !currentId) return;
    let live = true;
    markRecentPatient(api, currentId).then(
      () => live && void queryClient.invalidateQueries({ queryKey: RECENT_KEY }),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [familiar, canView, currentId, queryClient]);

  /* ---------- selecting ---------- */
  /** `patient.open.<target>` waiting for the dialog's choice. */
  const pending = useRef<PatientOpenTarget | null>(null);

  const identity = useIdentitySession();
  const canTxPlan = usePermission('txplan.view');
  const landing = defaultOpenTarget(identity?.roles.map((r) => r.key) ?? [], canTxPlan);

  const go = useCallback(
    (t: RouteTarget) =>
      void navigate({
        to: t.to as never,
        ...(t.params ? { params: t.params as never } : {}),
        search: t.search as never,
      }),
    [navigate],
  );

  const select = useCallback(
    (patientId: string, options?: { stay?: boolean }) => {
      setOpen(false);
      const officeParam = (location.search as Record<string, unknown>)['office'];
      const target = pending.current;
      pending.current = null;
      if (target) {
        go(openTargetRoute(target, patientId, officeParam));
        return;
      }
      const k = known.current.get(patientId);
      if (options?.stay) {
        if (k) setCurrentPatient({ id: patientId, label: k.label, chartNumber: k.chartNumber });
        const same = samePlaceFor(
          location.pathname,
          location.search as Record<string, unknown>,
          patientId,
        );
        if (same) go(same);
        // Without a label the strip could not name them: open the record instead.
        if (same || k) return;
      }
      go(openTargetRoute(landing, patientId, officeParam));
    },
    [go, landing, location.pathname, location.search],
  );

  const onOpenChange = useCallback((next: boolean) => {
    if (!next) pending.current = null;
    setOpen(next);
  }, []);

  /* ---------- search ---------- */
  const search = useCallback(
    async (
      q: string,
      signal: AbortSignal,
      options?: SelectPatientSearchOptions,
    ): Promise<readonly SelectPatientHit[]> => {
      const criteria = selectCriteria(q, options);
      // Nothing typed: nothing to ask, unless the mode asks for everyone (Get All).
      if (criteria.length === 0 && !isGetAll(q, options)) return [];
      try {
        const res = await fetchSelectPatients(
          clientFor(office.param),
          criteria,
          options ?? {},
          signal,
        );
        remember(res.hits);
        return res.hits.map((r) => rowToHit(r, mask));
      } catch (e) {
        // Demo mode and older APIs only have the top-bar search (no Get All).
        const old = e instanceof ApiError && e.status === 404;
        if (old && criteria.length === 0) return [];
        if (!old || q.trim().length < 2) throw e;
        const res = await getShellPatients(clientFor(office.param), {
          query: { q, limit: 25 },
          signal,
        });
        return res.hits.map((h) => {
          known.current.set(h.id, { label: selectLabel(h), chartNumber: h.chartNumber });
          return {
            id: h.id,
            label: mask(selectLabel(h)),
            chartNumber: h.chartNumber,
            birthdate: h.birthdate,
            status: h.status,
            lastName: mask(h.lastName),
            firstName: mask(h.firstName),
            preferredName: h.preferredName === null ? null : mask(h.preferredName),
          };
        });
      }
    },
    [office.param, mask, remember],
  );

  const recent = useMemo<readonly SelectPatientHit[]>(
    () =>
      serverRows
        ? serverRows.map((r) => rowToHit(r, mask))
        : sessionRecent.map((p) => ({
            id: p.id,
            label: mask(p.label),
            chartNumber: p.chartNumber,
            birthdate: null,
            status: '',
          })),
    [serverRows, sessionRecent, mask],
  );
  const searchFields = useMemo(
    () => SELECT_PATIENT_FIELDS.filter((f) => f !== 'ssn' || canSsn),
    [canSsn],
  );

  /* ---------- actions ---------- */
  const latest = useRef({ dialog, onPalette, select, recent, go, location });
  latest.current = { dialog, onPalette, select, recent, go, location };
  const openSelect = useCallback(() => {
    if (latest.current.dialog) setOpen(true);
    else latest.current.onPalette();
  }, []);

  useEffect(
    () =>
      registerAction({
        id: 'patient.select',
        label,
        labelKey: 'selectPatient',
        icon: 'user-round',
        permission: 'patient.view',
        run: openSelect,
      }),
    [label, openSelect],
  );

  const lastId = lastPatientId(recent, currentId);
  useEffect(
    () =>
      registerAction({
        id: 'patient.last',
        label: 'Last patient',
        icon: 'rotate-ccw',
        permission: 'patient.view',
        enabled: canView && lastId !== null,
        run: () => {
          const id = lastPatientId(latest.current.recent, getCurrentPatient()?.id ?? null);
          if (id) latest.current.select(id, { stay: true });
          else openSelect();
        },
      }),
    [canView, lastId, openSelect],
  );

  const routes = router.routesByPath as Record<string, unknown>;
  const available = PATIENT_OPEN_TARGET_IDS.filter(
    (t) => canView && canTarget[t] && routes[PATIENT_OPEN_TARGETS[t].path] !== undefined,
  ).join(',');
  useEffect(() => {
    const offs = (available ? available.split(',') : []).map((t) => {
      const target = t as PatientOpenTarget;
      return registerAction({
        id: `patient.open.${target}`,
        label: PATIENT_OPEN_TARGETS[target].label,
        permission: PATIENT_OPEN_TARGETS[target].permission,
        run: () => {
          const p = getCurrentPatient();
          const L = latest.current;
          if (p) {
            L.go(
              openTargetRoute(
                target,
                p.id,
                (L.location.search as Record<string, unknown>)['office'],
              ),
            );
            return;
          }
          // No patient: choose one first, then open the target for them.
          if (L.dialog) pending.current = target;
          openSelect();
        },
      });
    });
    return () => offs.forEach((off) => off());
  }, [available, openSelect]);

  useEffect(
    () =>
      registerAction({
        id: 'patients.newFamily',
        label: 'New family',
        icon: 'users-round',
        permission: 'patient.edit',
        enabled: canEdit,
        run: () =>
          latest.current.go({
            to: '/patients',
            search: {
              office: (latest.current.location.search as Record<string, unknown>)['office'],
              new: '1',
            },
          }),
      }),
    [canEdit],
  );

  if (!dialog) return null;
  const model: SelectPatientModel = {
    open,
    onOpenChange,
    onSelect: select,
    search,
    recent,
    searchFields,
  };
  return (
    <SkinSlot name="shell.selectPatient" model={model}>
      {null}
    </SkinSlot>
  );
}
